import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { ShieldCheck, FloppyDisk } from "@phosphor-icons/react";

const ROLES = [
  ["cashier", "Billing Executive (cashier)"],
  ["store_manager", "Store Manager"],
  ["operator", "Store Operator"],
  ["warehouse_manager", "Warehouse Manager"],
];

export default function DiscountPolicySection({ isAdmin }) {
  const [policy, setPolicy] = useState(null);
  const [limits, setLimits] = useState({});
  const [requireReason, setRequireReason] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/settings/discount-policy").then((r) => {
      setPolicy(r.data); setLimits(r.data.limits || {}); setRequireReason(!!r.data.require_reason);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const r = await api.patch("/settings/discount-policy", { limits, require_reason: requireReason });
      setPolicy(r.data); setLimits(r.data.limits); setMsg("Saved");
      setTimeout(() => setMsg(""), 2500);
    } catch (e) { setMsg(e.response?.data?.detail?.message || e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  if (!policy) return null;
  return (
    <div className="mt-8 bg-white border border-slate-200 rounded-md p-6" data-testid="discount-policy-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0"><ShieldCheck size={22} weight="duotone" /></div>
          <div>
            <h2 className="font-display text-lg font-bold">Discount & Pricing Guardrails</h2>
            <p className="text-xs text-slate-500 mt-0.5 max-w-2xl">
              Maximum discount each role may give on a bill (checked on the whole bill <i>and</i> on every line, as % of gross).
              Above the limit the POS asks a Store Manager / Admin to approve on-screen (5-minute, single-use approval, audited).
              Selling prices can only be changed by users with the <b>“Change selling price in POS”</b> permission (Store Managers and Admins by default).
            </p>
          </div>
        </div>
        {isAdmin && (
          <button onClick={save} disabled={saving} data-testid="discount-policy-save" className="inline-flex items-center gap-2 h-10 px-4 bg-slate-900 text-white text-sm font-semibold rounded-md hover:bg-slate-800 disabled:opacity-50">
            <FloppyDisk size={16} /> {saving ? "Saving…" : "Save policy"}
          </button>
        )}
      </div>
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {ROLES.map(([role, label]) => (
          <div key={role} className="border border-slate-200 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
            <div className="mt-2 flex items-center gap-2">
              <input type="number" min="0" max="100" step="0.5" disabled={!isAdmin} value={limits[role] ?? 0}
                onChange={(e) => setLimits({ ...limits, [role]: e.target.value })}
                data-testid={`discount-limit-${role}`}
                className="w-24 h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num text-right disabled:bg-slate-50" />
              <span className="text-sm text-slate-600">% max</span>
            </div>
          </div>
        ))}
        <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Super Admin</div>
          <div className="mt-2 text-sm font-mono-num font-semibold">100 % · no limit</div>
        </div>
      </div>
      <label className="mt-4 inline-flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" disabled={!isAdmin} checked={requireReason} onChange={(e) => setRequireReason(e.target.checked)} data-testid="discount-require-reason" className="w-4 h-4" />
        Require a reason whenever a discount or price change is given (stored on the invoice and in the audit log)
      </label>
      {msg && <div className="mt-3 text-xs font-semibold text-emerald-700" data-testid="discount-policy-msg">{msg}</div>}
    </div>
  );
}
